import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Tooltip, Button, Paper } from '@mui/material';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EmailIcon from '@mui/icons-material/Email';
import ShareIcon from '@mui/icons-material/Share';
import SensorsIcon from '@mui/icons-material/Sensors';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import VideoChatIcon from '@mui/icons-material/VideoChat';
import BallotIcon from '@mui/icons-material/Ballot';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArticleIcon from '@mui/icons-material/Article';
import LockIcon from '@mui/icons-material/Lock';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { UserData } from 'types/userInfoTypes';
import AppShowcase from 'pages/login/AppShowcase';
import streamLogo from 'assets/images/stream.png';
import styles from './home.module.css';

interface AppInfo {
	id: string;
	name: string;
	icon: React.ReactNode;
	description: string;
	route: string;
	learnMoreUrl: string;
	rolePrefix: string;
	// Extra role-group names for edge cases where specific clients have special roles like CAMfm
	// (case-insensitive). For clients whose group names don't follow the
	// CAN<app> convention.
	extraRoleGroups?: string[];
	externalRoute?: boolean; // true if route is outside /canappsv3/ base
}

const allApps: AppInfo[] = [
	{
		id: 'canmail',
		name: 'CANmail',
		icon: <EmailIcon />,
		description: 'Send & manage targeted mail campaigns',
		route: '/emailhistory',
		learnMoreUrl: 'https://streamdata.com/canmail',
		rolePrefix: 'CANmail',
	},
	{
		id: 'canmeet',
		name: 'CANmeet',
		icon: <VideoChatIcon />,
		description: 'Meeting scheduling & collaboration',
		route: '/meet',
		learnMoreUrl: 'https://streamdata.com/canmeet',
		rolePrefix: 'CANmeet',
	},
	{
		id: 'cantrack',
		name: 'CANtrack',
		icon: <TrackChangesIcon />,
		description: 'Project management with tasks & timesheets',
		route: '/cantrack/cases',
		learnMoreUrl: 'https://streamdata.com/cantrack',
		rolePrefix: 'CANtrack',
	},
	{
		id: 'candispatch',
		name: 'CANdispatch',
		icon: <EmojiTransportationIcon />,
		description: 'Real-time patrol guard tracking & analytics',
		route: '/dispatch/guard-locations',
		learnMoreUrl: 'https://streamdata.com/candispatch',
		rolePrefix: 'CANdispatch',
	},
	{
		id: 'canpark',
		name: 'CANpark',
		icon: <DirectionsCarIcon />,
		description: 'Parking enforcement platform',
		route: '/canpark',
		learnMoreUrl: 'https://streamdata.com/canpark',
		rolePrefix: 'CANpark',
	},
	{
		id: 'canshare',
		name: 'CANshare',
		icon: <ShareIcon />,
		description: 'Secure file sharing & collaboration',
		route: '/documents',
		learnMoreUrl: 'https://streamdata.com/canshare',
		rolePrefix: 'CANshare',
	},
	{
		id: 'canai',
		name: 'CANai',
		icon: <PsychologyIcon />,
		description: 'AI chatbot trained on your data',
		route: '/canai',
		learnMoreUrl: 'https://streamdata.com/canquery',
		rolePrefix: 'CANai',
		externalRoute: true,
	},
	{
		id: 'cansense',
		name: 'CANsense',
		icon: <SensorsIcon />,
		description: 'Infrastructure monitoring & alerts',
		route: '/sense',
		learnMoreUrl: 'https://streamdata.com/cansense',
		rolePrefix: 'CANsense',
	},
	{
		id: 'cansurvey',
		name: 'CANsurvey',
		icon: <BallotIcon />,
		description: 'Create & analyze surveys',
		route: '/surveyhome',
		learnMoreUrl: 'https://streamdata.com/cansurvey',
		rolePrefix: 'CANsurvey',
		extraRoleGroups: ['CAMFM_appsAdmin', 'CAMFM_appsUser'],
	},
	{
		id: 'canstore',
		name: 'CANstore',
		icon: <StorefrontIcon />,
		description: 'Complete eCommerce platform',
		route: '/can-store',
		learnMoreUrl: 'https://streamdata.com/canstore',
		rolePrefix: 'CANstore',
	},
	{
		id: 'canblog',
		name: 'CANblog',
		icon: <ArticleIcon />,
		description: 'Create & publish blog content',
		route: '/canblog',
		learnMoreUrl: 'https://streamdata.com/blog',
		rolePrefix: 'CANblog',
	},
];

const Home: React.FC = () => {
	const navigate = useNavigate();
	const { userInfo }: { userInfo: UserData | null } = useSelector(selectUserInfo);

	const isSubscribed = (app: AppInfo): boolean => {
		if (!userInfo?.userRoleGroups) return false;
		const prefix = app.rolePrefix.toLowerCase();
		const extras = (app.extraRoleGroups ?? []).map((name) => name.toLowerCase());
		return userInfo.userRoleGroups.some((role) => {
			const name = role.name.toLowerCase();
			return name.includes(prefix) || extras.includes(name);
		});
	};

	const getDispatchRoute = (): string => {
		if (!userInfo?.userRoleGroups) return '/dispatch/guard-locations';

		const hasClientRole = userInfo.userRoleGroups.some(
			(role) => role.name === 'CANdispatch_Client'
		);
		if (hasClientRole) return '/activity-report';

		const hasGuardRole = userInfo.userRoleGroups.some(
			(role) => role.name === 'CANdispatch_Guard'
		);
		if (hasGuardRole) return '/canguard';

		return '/dispatch/guard-locations';
	};

	const handleAppClick = (app: AppInfo, subscribed: boolean) => {
		if (subscribed) {
			// Special handling for CANdispatch - route based on user role
			if (app.id === 'candispatch') {
				navigate(getDispatchRoute());
				return;
			}

			if (app.externalRoute) {
				// For routes outside /canappsv3/ base, use full URL with current origin
				window.location.href = `${window.location.origin}${app.route}`;
			} else {
				navigate(app.route);
			}
		} else {
			window.open(app.learnMoreUrl, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<div className={styles.page}>
			{/* Left side - Carousel */}
			<div className={styles.showcaseSide}>
				<Paper className={styles.showcasePaper} elevation={3}>
					<AppShowcase />
				</Paper>
			</div>

			{/* Right side - Apps Grid */}
			<div className={styles.appsSide}>
				<div className={styles.appsContainer}>
					<img src={streamLogo} alt="Stream Data Systems" className={styles.logo} />
					<div className={styles.header}>
						<Typography variant="h5" className={styles.headerTitle}>
							Welcome to CANapps 4, {userInfo?.firstName || 'User'}
						</Typography>
						<Typography variant="body2" className={styles.headerSubtitle}>
							Click on an app to open or learn more
						</Typography>
					</div>

					<div className={styles.appsGrid} data-tour="home-apps">
						{allApps.map((app) => {
							const subscribed = isSubscribed(app);
							return subscribed ? (
								<Card
									key={app.id}
									className={styles.appCard}
									onClick={() => handleAppClick(app, true)}
								>
									<CardContent className={styles.cardContent}>
										<Box className={styles.iconContainer}>
											{app.icon}
										</Box>
										<Typography variant="subtitle1" className={styles.appName}>
											{app.name}
										</Typography>
										<Typography variant="caption" className={styles.appDescription}>
											{app.description}
										</Typography>
									</CardContent>
								</Card>
							) : (
								<Tooltip
									key={app.id}
									title="Not subscribed — Click to learn more"
									arrow
									placement="top"
								>
									<Card
										className={`${styles.appCard} ${styles.disabledCard}`}
										onClick={() => handleAppClick(app, false)}
									>
										<CardContent className={styles.cardContent}>
											<Box className={styles.iconContainerDisabled}>
												{app.icon}
												<LockIcon className={styles.lockIcon} />
											</Box>
											<Typography variant="subtitle1" className={styles.appNameDisabled}>
												{app.name}
											</Typography>
											<Typography variant="caption" className={styles.appDescriptionDisabled}>
												{app.description}
											</Typography>
										</CardContent>
									</Card>
								</Tooltip>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Home;
